import { BracketsSquare } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { DraggableItemShell } from '../dnd/DraggableItemShell';
import type { ReservedFolderType } from '../dnd/types';
import { ActionMenu } from './ActionMenu';
import { Item } from './Item';
import { FluxObject, useAgentsSidebarContext } from './context';
import { useSidebarItemDraggable } from './useSidebarItemDraggable';

interface FluxBotItemProps {
  object: FluxObject;
  isNested?: boolean;
  spaceId?: string | null;
  parentId?: string | null;
  ancestorReservedType?: ReservedFolderType | null;
  onEdit?: (object: FluxObject) => void;
  onRename?: (object: FluxObject) => void;
  onDelete?: (object: FluxObject) => void;
}

const FluxBotItemComponent: React.FC<FluxBotItemProps> = ({
  object,
  isNested = true,
  spaceId = null,
  parentId,
  ancestorReservedType = null,
  onEdit,
  onRename,
  onDelete,
}) => {
  const { activeObjectId, onObjectClick } = useAgentsSidebarContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hasOpened, setHasOpened] = useState(false);

  const draggable = useSidebarItemDraggable({
    itemId: object.id,
    kind: 'object',
    parentId: parentId ?? object.folderId ?? null,
    spaceId,
    label: object.name,
    ancestorReservedType,
  });

  const handleMenuClick = (anchor: HTMLElement) => {
    setHasOpened(true);
    setAnchorEl(anchor);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <DraggableItemShell
      ref={draggable.setNodeRef}
      isDragging={draggable.isDragging}
      dropAbove={draggable.showDropAbove}
      dropBelow={draggable.showDropBelow}
    >
      <Item
        icon={<BracketsSquare size={16} />}
        label={object.name}
        isActive={activeObjectId === object.id}
        isNested={isNested}
        onClick={() => onObjectClick(object)}
        onMenuClick={handleMenuClick}
      />
      {(anchorEl || hasOpened) && (
        <ActionMenu
          anchorEl={anchorEl}
          onClose={handleClose}
          onEdit={onEdit ? () => onEdit(object) : undefined}
          onRename={onRename ? () => onRename(object) : undefined}
          onDelete={onDelete ? () => onDelete(object) : undefined}
          showEdit={true}
        />
      )}
    </DraggableItemShell>
  );
};

export const FluxBotItem = React.memo(FluxBotItemComponent);
